import { useEffect, useRef } from "react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Textarea } from "@/components/ui/textarea";
import { Info } from "lucide-react";
import { AlignError } from "@/constants";

type TextAreaInputProps = {
  id?: string;
  placeholder?: string;
  className?: string;
  error?: string;
  value: string;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  disabled?: boolean;
  autoResize?: boolean;
  alignError?: AlignError;
  maxLength?: number;
  "data-testid": string;
};

const TextAreaInput = ({
  id,
  placeholder,
  className,
  value,
  error,
  onChange,
  onBlur,
  disabled,
  autoResize = false,
  alignError = AlignError.CENTER,
  maxLength,
  "data-testid": dataTestId,
}: TextAreaInputProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!autoResize || !textareaRef.current) return;
    textareaRef.current.style.height = "0px";
    textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 7}px`;
    textareaRef.current.style.paddingTop = "12px";
  }, [value, autoResize]);

  return (
    <div className="relative flex flex-row items-center space-x-2">
      <Textarea
        ref={textareaRef}
        id={id}
        data-testid={dataTestId}
        className={`${className} pr-5`}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={() => onBlur?.(value)}
        disabled={disabled}
        {...(maxLength !== undefined ? { maxLength } : {})}
      />

      {error && (
        <HoverCard>
          <HoverCardTrigger asChild>
            <Info
              className="absolute right-5 top-3 cursor-pointer text-[11px] text-red-400"
              size={16}
            />
          </HoverCardTrigger>
          <HoverCardContent align={alignError}>
            <em className="text-xs text-red-400">{error}</em>
          </HoverCardContent>
        </HoverCard>
      )}
    </div>
  );
};

export default TextAreaInput;
